/* Dashboard Page — Mission control 30-second overview
   Uses Phase 4.1 components with container-based rendering */

import { renderMarketStatusBar, initMarketStatusBar } from '../components/MarketStatusBar';
import { renderFIIDIIPanel } from '../components/FIIDIIPanel';
import { renderStrategyCard } from '../components/StrategyCard';
import { renderQuickStats } from '../components/QuickStats';
import { renderNewsTicker } from '../components/NewsTicker';
import { renderLiveTV } from '../components/LiveTV';
import { navigate } from '../router/router';

export function renderDashboard(): string {
  const statusBar = renderMarketStatusBar();

  return `
    <section class="dashboard-v3">
      <header class="dashboard-v3__hero">
        <div class="dashboard-v3__hero-copy">
          <p class="dashboard-v3__eyebrow">Market Mission Control</p>
          <h1 class="dashboard-v3__title">India Session Pulse</h1>
          <p class="dashboard-v3__subtitle">A modern market desk with classic board discipline: strategy, breadth, flows, and headlines arranged for fast reading.</p>
          <div class="dashboard-v3__hero-note">
            <span class="dashboard-v3__hero-note-label">Today&apos;s Read</span>
            <p>Use the opening tape for confirmation, watch institutional flow for conviction, and let the headline tape explain where the next rotation is building.</p>
          </div>
        </div>

        <div class="dashboard-v3__hero-side">
          <div class="dashboard-v3__brief-grid" aria-label="Dashboard summary">
            <article class="dashboard-v3__brief-card">
              <span class="dashboard-v3__brief-label">Board Style</span>
              <strong class="dashboard-v3__brief-value">Modern Classic</strong>
              <span class="dashboard-v3__brief-sub">Clean hierarchy, low-noise surfaces</span>
            </article>
            <article class="dashboard-v3__brief-card">
              <span class="dashboard-v3__brief-label">Primary Lens</span>
              <strong class="dashboard-v3__brief-value">Trend + Breadth</strong>
              <span class="dashboard-v3__brief-sub">Signal first, explanation second</span>
            </article>
            <article class="dashboard-v3__brief-card">
              <span class="dashboard-v3__brief-label">Institutional Cue</span>
              <strong class="dashboard-v3__brief-value">FII / DII Tape</strong>
              <span class="dashboard-v3__brief-sub">Follow where size is leaning</span>
            </article>
            <article class="dashboard-v3__brief-card">
              <span class="dashboard-v3__brief-label">Narrative Feed</span>
              <strong class="dashboard-v3__brief-value">Priority Headlines</strong>
              <span class="dashboard-v3__brief-sub">Only market-moving stories stay visible</span>
            </article>
          </div>

          <div class="dashboard-v3__actions" role="navigation" aria-label="Dashboard quick navigation">
            <button type="button" class="dashboard-v3__action" data-path="/session-radar">Open Session Radar</button>
            <button type="button" class="dashboard-v3__action" data-path="/markets">Open Markets</button>
            <button type="button" class="dashboard-v3__action" data-path="/strategy">Open Strategy</button>
            <button type="button" class="dashboard-v3__action" data-path="/intelligence">Open Intelligence</button>
          </div>
        </div>
      </header>

      <section class="dashboard-v3__status-shell">
        <div class="dashboard-v3__section-head">
          <div>
            <p class="dashboard-v3__section-kicker">Market Tape</p>
            <h2 class="dashboard-v3__section-title">Session Status</h2>
          </div>
          <span class="dashboard-v3__section-meta">Live core indices and market clock</span>
        </div>
        ${statusBar}
      </section>

      <section class="dashboard-v3__board">
        <div class="dashboard-v3__board-topbar">
          <div class="dashboard-v3__section-head">
          <div>
            <p class="dashboard-v3__section-kicker">Tactical Desk</p>
            <h2 class="dashboard-v3__section-title">Opening Board</h2>
          </div>
          <span class="dashboard-v3__section-meta">Strategy lead, live movement context, and fast headline read</span>
        </div>

          <nav class="dashboard-v3__switcher" aria-label="Dashboard sections">
            <button type="button" class="dashboard-v3__switcher-btn dashboard-v3__switcher-btn--active" data-dashboard-view-trigger="overview">Overview</button>
            <button type="button" class="dashboard-v3__switcher-btn" data-dashboard-view-trigger="internals">Internals</button>
            <button type="button" class="dashboard-v3__switcher-btn" data-dashboard-view-trigger="broadcast">Broadcast</button>
            <button type="button" class="dashboard-v3__switcher-btn" data-dashboard-view-trigger="all">All</button>
          </nav>
        </div>

        <div class="grid-dashboard dashboard-v3__grid" id="dashboard-grid" data-dashboard-view="overview">
          <section id="dash-strategy" class="dashboard-v3__tile dashboard-v3__tile--strategy"></section>
          <section id="dash-headlines" class="dashboard-v3__tile dashboard-v3__tile--headlines"></section>
          <section id="dash-quick-stats" class="dashboard-v3__tile dashboard-v3__tile--stats"></section>
          <section id="dash-fii-dii" class="dashboard-v3__tile dashboard-v3__tile--flows"></section>
          <section id="dash-live-tv" class="dashboard-v3__tile dashboard-v3__tile--tv"></section>
        </div>
      </section>
    </section>
  `;
}

function bindDashboardViewSwitcher(): void {
  const grid = document.getElementById('dashboard-grid');
  if (!grid) return;

  document.querySelectorAll<HTMLButtonElement>('[data-dashboard-view-trigger]').forEach((button) => {
    button.addEventListener('click', () => {
      const nextView = button.dataset.dashboardViewTrigger;
      if (!nextView) return;

      grid.setAttribute('data-dashboard-view', nextView);
      document.querySelectorAll<HTMLButtonElement>('[data-dashboard-view-trigger]').forEach((node) => {
        node.classList.toggle('dashboard-v3__switcher-btn--active', node === button);
      });
    });
  });
}

export function initDashboard(): void {
  initMarketStatusBar();

  const el = (id: string) => document.getElementById(id);

  const stratEl = el('dash-strategy');
  if (stratEl) renderStrategyCard(stratEl);

  const fiiEl = el('dash-fii-dii');
  if (fiiEl) renderFIIDIIPanel(fiiEl);

  const statsEl = el('dash-quick-stats');
  if (statsEl) renderQuickStats(statsEl);

  const headEl = el('dash-headlines');
  if (headEl) renderNewsTicker(headEl);

  const tvEl = el('dash-live-tv');
  if (tvEl) renderLiveTV(tvEl);

  document.querySelectorAll('.dashboard-v3__action').forEach((button) => {
    button.addEventListener('click', () => {
      const path = button.getAttribute('data-path');
      if (path) navigate(path);
    });
  });

  bindDashboardViewSwitcher();
}
